// Free / disposable email domain detection for the Request Access form.
//
// Mirrored server-side in `b2b-api/src/utils/workEmail.ts`, which is the real
// gate: this copy is only a fast-path form hint, and the API rejects a personal
// domain regardless. Keep the two lists in sync — a domain added there must be
// added here, or the form silently accepts something the API will reject.
//
// Full-domain matching, deliberately. Matching only the first domain label
// (e.g. 'mail' in mail.acme.com) false-positives legitimate corporate hosts.

export const FREE_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
  // Google
  'gmail.com',
  'googlemail.com',
  // Microsoft
  'outlook.com',
  'outlook.co.uk',
  'outlook.de',
  'outlook.fr',
  'outlook.es',
  'outlook.it',
  'outlook.com.au',
  'hotmail.com',
  'hotmail.co.uk',
  'hotmail.fr',
  'hotmail.de',
  'hotmail.es',
  'hotmail.it',
  'hotmail.nl',
  'hotmail.be',
  'hotmail.ca',
  'hotmail.com.br',
  'live.com',
  'live.co.uk',
  'live.nl',
  'live.fr',
  'live.de',
  'live.ca',
  'live.com.au',
  'msn.com',
  'passport.com',
  // Apple
  'icloud.com',
  'me.com',
  'mac.com',
  // Yahoo
  'yahoo.com',
  'yahoo.co.uk',
  'yahoo.co.jp',
  'yahoo.co.in',
  'yahoo.ca',
  'yahoo.com.au',
  'yahoo.com.br',
  'yahoo.com.mx',
  'yahoo.de',
  'yahoo.fr',
  'yahoo.es',
  'yahoo.it',
  'yahoo.gr',
  'yahoo.in',
  'ymail.com',
  'rocketmail.com',
  // AOL / Verizon
  'aol.com',
  'aol.co.uk',
  'aim.com',
  'verizon.net',
  // Proton
  'proton.me',
  'protonmail.com',
  'protonmail.ch',
  'pm.me',
  // GMX / United Internet
  'gmx.com',
  'gmx.de',
  'gmx.net',
  'gmx.at',
  'gmx.ch',
  'gmx.co.uk',
  'gmx.fr',
  'gmx.es',
  'web.de',
  'mail.com',
  'email.com',
  'usa.com',
  // Russia / CIS
  'yandex.ru',
  'yandex.com',
  'ya.ru',
  'mail.ru',
  'inbox.ru',
  'bk.ru',
  'list.ru',
  'internet.ru',
  'rambler.ru',
  'ukr.net',
  // Asia
  'qq.com',
  'foxmail.com',
  '163.com',
  '126.com',
  'yeah.net',
  'sina.com',
  'sina.cn',
  'sohu.com',
  'aliyun.com',
  'naver.com',
  'hanmail.net',
  'daum.net',
  'nate.com',
  'rediffmail.com',
  'docomo.ne.jp',
  'ezweb.ne.jp',
  // Other consumer providers
  'zoho.com',
  'zohomail.com',
  'fastmail.com',
  'fastmail.fm',
  'hushmail.com',
  'tutanota.com',
  'tutanota.de',
  'tuta.io',
  'tuta.com',
  'startmail.com',
  'mailbox.org',
  'posteo.de',
  'runbox.com',
  'lycos.com',
  'excite.com',
  'juno.com',
  'netzero.net',
  'earthlink.net',
  'comcast.net',
  'att.net',
  'sbcglobal.net',
  'bellsouth.net',
  'cox.net',
  'charter.net',
  'shaw.ca',
  'rogers.com',
  'sympatico.ca',
  'telus.net',
  'btinternet.com',
  'sky.com',
  'virginmedia.com',
  'talktalk.net',
  'ntlworld.com',
  'blueyonder.co.uk',
  'orange.fr',
  'wanadoo.fr',
  'free.fr',
  'laposte.net',
  'sfr.fr',
  'libero.it',
  'virgilio.it',
  'alice.it',
  'tin.it',
  'terra.com.br',
  'uol.com.br',
  'bol.com.br',
  'seznam.cz',
  'wp.pl',
  'o2.pl',
  'onet.pl',
  'interia.pl',
  't-online.de',
  'freenet.de',
  'arcor.de',
  'bigpond.com',
  'optusnet.com.au',
  'iinet.net.au',
  'xtra.co.nz',
  'telkomsa.net'
]);

export const DISPOSABLE_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
  'mailinator.com',
  'mailinator.net',
  '10minutemail.com',
  '10minutemail.net',
  '20minutemail.com',
  'guerrillamail.com',
  'guerrillamail.net',
  'guerrillamail.org',
  'guerrillamail.biz',
  'guerrillamail.de',
  'grr.la',
  'sharklasers.com',
  'temp-mail.org',
  'temp-mail.io',
  'tempmail.com',
  'tempmail.net',
  'tempmailo.com',
  'tempr.email',
  'throwawaymail.com',
  'throwaway.email',
  'trashmail.com',
  'trashmail.de',
  'trash-mail.com',
  'dispostable.com',
  'getnada.com',
  'nada.email',
  'yopmail.com',
  'yopmail.fr',
  'yopmail.net',
  'maildrop.cc',
  'mailnesia.com',
  'mytemp.email',
  'moakt.com',
  'inboxkitten.com',
  'emailondeck.com',
  'fakeinbox.com',
  'spamgourmet.com',
  'mailcatch.com',
  'mintemail.com',
  'anonaddy.me',
  'anonaddy.com',
  'simplelogin.com',
  'simplelogin.io',
  'duck.com',
  'mozmail.com',
  'relay.firefox.com',
  'burnermail.io',
  'harakirimail.com',
  'discard.email',
  'mailde.de',
  'wegwerfmail.de',
  'byom.de',
  'cock.li',
  'tempinbox.com',
  'jetable.org',
  'spam4.me',
  'linshiyouxiang.net',
  'mailtemp.top',
  'email-temp.com',
  'minuteinbox.com',
  'luxusmail.org',
  'vomoto.com'
]);

export const WORK_EMAIL_ERROR =
  'Seems you are using your personal email. Please use your company email to get access straight away.';

const EMAIL_RE = /^[^\s@]+@([^\s@]+\.[^\s@]+)$/;

export const emailDomain = (email: string): string | null => {
  const match = EMAIL_RE.exec(email.trim().toLowerCase());
  return match ? match[1] : null;
};

export const isFreeEmailDomain = (email: string): boolean => {
  const domain = emailDomain(email);
  if (domain === null) return false;
  return FREE_EMAIL_DOMAINS.has(domain) || DISPOSABLE_EMAIL_DOMAINS.has(domain);
};

export const isEmailShaped = (email: string): boolean =>
  EMAIL_RE.test(email.trim());

// Best-effort company name from the email domain. Returns '' for personal
// domains so the field stays empty rather than suggesting "Gmail".
export const extractCompanyName = (email: string): string => {
  const match = email.match(/@([^.]+)\./);
  if (!match) return '';
  if (isFreeEmailDomain(email)) return '';
  const label = match[1].toLowerCase();
  return label.charAt(0).toUpperCase() + label.slice(1);
};
